import React, { FC, useContext, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    RenewalLineView,
    SimpleRenewalTile,
    SimpleRenewalUsageLimit,
    SimpleRenewalUsageLimitValue
} from '../../utils/renewalTableTileTypes';
import { RenewalLineCell } from './RenewalLineCell';
import { SelectionContext } from '../../context/SelectionContext';
import { ProductCoverCell } from '../ProductCoverCell';
import { IssueUsageStatisticsCell } from '../../../../shared/subscription-management/IssueUsageStatisticsCell';
import MaterialIcon from '../../../../shared/common/MaterialIcon';
import { ProductCopyStockDropdown } from '../../../../shared/product/ProductCopyStock';
import { useTranslation } from 'react-i18next';
import { LOANFORMAT_EPRESS_OBJECT } from '../../../../shared/loanFormat/constants';

export const SimpleRenewalTableTile: FC<SimpleRenewalTile> = props => {
    const {
        product,
        currentYearSubscriptions,
        nextYearRenewalLine,
        previousIssueUsage,
        currentIssueUsage,
        showWarning,
        userSubscribedLabel
    } = props;
    const { title } = product;
    const { t } = useTranslation();
    const { isInSelectionMode, isSelectAll } = useContext(SelectionContext);
    const productLink = !isInSelectionMode ? `/products/${product.id}` : '#';

    return (
        <tr className="table-tile">
            <ProductCoverCell product={product} isInSelectionMode={isInSelectionMode} isSelectAll={isSelectAll} />
            <td className="product-title-cell">
                <div>
                    <div>
                        <Link to={productLink}>
                            <b>{title}</b>
                        </Link>
                        {showWarning ? <MaterialIcon icon="warning" className={showWarning} /> : null}
                    </div>
                    <small>{userSubscribedLabel ? userSubscribedLabel : null}</small>
                </div>
            </td>

            <td className="current-year-subscriptions">
                {(() => {
                    switch (currentYearSubscriptions.type) {
                        case SimpleRenewalUsageLimit.Value:
                            return <SimpleCurrentYearSubscriptionsUsageLimitValue {...currentYearSubscriptions} />;
                        case SimpleRenewalUsageLimit.None:
                            return <small>{currentYearSubscriptions.label}</small>;
                        default:
                            return <></>;
                    }
                })()}
            </td>
            <td>{previousIssueUsage ? <IssueUsageStatisticsCell {...previousIssueUsage} /> : null}</td>
            <td>{currentIssueUsage ? <IssueUsageStatisticsCell {...currentIssueUsage} /> : null}</td>
            {(() => {
                switch (nextYearRenewalLine.type) {
                    case RenewalLineView.RenewalLine:
                        return <RenewalLineCell {...nextYearRenewalLine} />;
                    case RenewalLineView.Renewed:
                        return (
                            <td colSpan={3} className="renewal-already-ordered">
                                <div>
                                    <b>{nextYearRenewalLine.label}</b>
                                    <div>{t('shop.subscriptions.alreadyOrdered')}</div>
                                </div>
                            </td>
                        );
                    case RenewalLineView.Unavailable:
                        return (
                            <td colSpan={3} className="renewal-unavailable">
                                <div>
                                    <b>{t('shop.subscriptions.unavailable')}</b>
                                </div>
                            </td>
                        );
                    default:
                        return <></>;
                }
            })()}
        </tr>
    );
};

const SimpleCurrentYearSubscriptionsUsageLimitValue: FC<SimpleRenewalUsageLimitValue> = ({ label, stockDetails }) => {
    const ref = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    return (
        <>
            <b ref={ref} onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
                {label}
            </b>
            <ProductCopyStockDropdown
                loanFormat={LOANFORMAT_EPRESS_OBJECT}
                stockDetails={stockDetails}
                placement="top-start"
                visible={visible}
                popoverRef={ref}
            />
        </>
    );
};
